import React, { useState } from "react";
import BackHeader from "@components/header/BackHeader";
import { useApiRequest } from "@utils/ApiRequest";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import { ProductQnAList } from "@pages/product/_components";
import ProductQnaInput from "@components/input/ProductQnaInput";
import { FormProvider, useForm } from "react-hook-form";
import useAxios from "@libs/axios/useAxios";
import { queryClient } from "@libs/reactQuery/queryClient";

const Container = styled.form({
  display: "flex",
  flexDirection: "column",
  marginBottom: "9rem",
});

const ProductInfo = styled.div({
  display: "flex",
  gap: "1rem",
  padding: "2rem",

  ".brand": {
    fontSize: "2rem",
    fontWeight: 600,
    lineHeight: "2rem",
  },

  ".title": {
    fontSize: "1.4rem",
    fontWeight: 400,
    lineHeight: "2.1rem",
  },
});

const ProductImage = styled.div({
  position: "relative",
  width: "6rem",
  minWidth: "6rem",
  objectFit: "cover",

  "::after": {
    content: `""`,
    display: "block",
    paddingBottom: "100%",
  },
});

const ImageItem = styled.div({
  width: "6rem",
  height: "6rem",

  position: "absolute",
});

const InfoBox = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const ProductQnA = () => {
  const { getProductDetail } = useApiRequest();
  const params = useParams();
  const { axiosInstance } = useAxios();
  const [selectedQnaId, setSelectedQnaId] = useState<null | number>(null);
  const lang = window.localStorage.getItem("language");

  const { data: productInfo, isLoading } = useQuery({
    queryKey: ["productInfo", params.productId],
    queryFn: () => getProductDetail(Number(params?.productId)),
    enabled: !!params.productId,
  });

  const method = useForm({
    defaultValues: {
      content: "",
    },
  });

  const { handleSubmit, setValue } = method;

  const onSubmit = async (data: any) => {
    if (!data.content.trim()) return;

    if (selectedQnaId === null) {
      const res = await axiosInstance.post(
        `/api/v1/product/${productInfo?.product_id}/qna`,
        {
          question: data.content,
          notification_language: lang,
        },
      );

      if (res.status === 200) {
        await queryClient.invalidateQueries({
          queryKey: ["productQnaList"],
        });
        setValue("content", "");
      }
    } else {
      const res = await axiosInstance.post(
        `/api/v1/product/${productInfo?.product_id}/qna/${selectedQnaId}/answer`,
        {
          answer: data.content,
          notification_language: lang,
        },
      );

      if (res.status === 200) {
        await queryClient.invalidateQueries({
          queryKey: ["productQnaList"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["productQnaAnswerList"],
        });
        setValue("content", "");
      }
    }
  };

  // const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
  //   if (e.key === "Enter") {
  //     e.preventDefault();
  //     e.stopPropagation();
  //     handleSubmit(onSubmit)();
  //   }
  // };

  return isLoading ? null : (
    <FormProvider {...method}>
      <Container onSubmit={handleSubmit(onSubmit)}>
        <BackHeader title={"문의"} />
        <ProductInfo>
          <ProductImage>
            <ImageItem>
              <Image
                src={productInfo?.product_image_list[0]?.image_url}
                width="6rem"
                height="6rem"
              />
            </ImageItem>
          </ProductImage>

          <InfoBox>
            <span className="brand">
              {productInfo[`brand_name_${lang}`] ||
                productInfo[`brand_name_ko`]}
            </span>

            <span className="title ellipsis-1">
              {productInfo[`product_name_${lang}`] ||
                productInfo[`product_name_ko`]}
            </span>
          </InfoBox>
        </ProductInfo>

        <ProductQnAList
          productId={productInfo?.product_id}
          selectedQnaId={selectedQnaId}
          setSelectedQnaId={setSelectedQnaId}
        />

        <ProductQnaInput
          selectedQnaId={selectedQnaId}
          setSelectedQnaId={setSelectedQnaId}
        />
      </Container>
    </FormProvider>
  );
};

export default ProductQnA;
